import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

import { endpoints } from "@/lib/api/endpoints";
import type {
  OffPremiseGridEntry,
  RestaurantSettings,
  TableGridArea,
} from "@/lib/api/types";
import { useI18n, useTranslate } from "@/lib/i18n";
import { hasPermission, PERMISSIONS, useSession } from "@/modules/auth/useSession";
import { canSendToKitchen } from "@/modules/pos/lib/kitchenRouting";
import { useOptionalRestaurantNotifications } from "@/modules/notifications/NotificationsProvider";
import { queryKeys, useApiQuery } from "@/shared/queries";
import { usePrivateToast } from "@/shared/usePrivateToast";


/** One bill a waiter has sent to the cashier, and where it sits. */
export type CashierRequest = {
  invoiceId: string;
  requestedAt: string;
  /** The table's name, or null for a takeaway or delivery bill. */
  tableName: string | null;
  dailySequence: number;
};

/** Every bill on the floor that is waiting for the cashier (D120). */
export function cashierRequestsOnFloor(
  areas: readonly TableGridArea[] | undefined,
  offPremise: readonly OffPremiseGridEntry[] | undefined,
): CashierRequest[] {
  const out: CashierRequest[] = [];
  for (const area of areas ?? []) {
    for (const table of area.tables) {
      for (const invoice of table.openInvoices) {
        if (!invoice.cashierRequestedAt) continue;
        out.push({
          invoiceId: invoice.invoiceId,
          requestedAt: invoice.cashierRequestedAt,
          tableName: table.name,
          dailySequence: invoice.dailySequence,
        });
      }
    }
  }
  for (const bill of offPremise ?? []) {
    if (!bill.cashierRequestedAt) continue;
    out.push({
      invoiceId: bill.invoiceId,
      requestedAt: bill.cashierRequestedAt,
      tableName: null,
      dailySequence: bill.dailySequence,
    });
  }
  return out;
}

/**
 * The requests that are NEW since the last look: a bill not seen waiting
 * before, or one asked again (a later stamp) after being sent and refilled.
 *
 * `seen` is null on the first look, which returns nothing: requests already
 * waiting when the screen loaded are on the floor's badges, and ringing for
 * all of them at once on every page load would teach people to mute it.
 */
export function freshCashierRequests(
  seen: ReadonlyMap<string, string> | null,
  current: readonly CashierRequest[],
): CashierRequest[] {
  if (seen === null) return [];
  return current.filter((request) => seen.get(request.invoiceId) !== request.requestedAt);
}

/**
 * D120 — THE CASHIER HEARS A WAITER'S "SEND TO CASHIER".
 *
 * Mounted once for the whole signed-in app, and active only for a session that
 * may send to the kitchen (`canSendToKitchen`, which honours the store's D121
 * switch): those are the people the waiter is asking. It reads the same floor the till already reads (the cache is
 * shared, so it adds no request while the floor is on screen) and rings the
 * notification chime plus a toast that opens the bill.
 *
 * The floor read rather than a push: the four push event types are a closed
 * set the worker validates strictly, and the floor already refreshes on every
 * committed floor revision (and on focus/reconnect), which is soon enough for
 * "the order is ready to send".
 */
export function CashierRequestAlerts() {
  const { data: session } = useSession();
  const mayFire = hasPermission(session, PERMISSIONS.ITEM_FIRE);
  const settings = useApiQuery<RestaurantSettings>(
    queryKeys.settings,
    endpoints.restaurant.settings,
    { enabled: mayFire, retry: false },
  );
  // Those the waiter is asking: whoever may send under the store's switch
  // (D121) — with it on, the cashier's rung, not a waiter's kitchen rung.
  const enabled =
    hasPermission(session, PERMISSIONS.POS_USE) &&
    canSendToKitchen(session, settings.data?.ordersViaCashier);

  const grid = useApiQuery<TableGridArea[]>(queryKeys.grid, endpoints.restaurant.grid, {
    enabled,
  });
  const offPremise = useApiQuery<OffPremiseGridEntry[]>(
    [...queryKeys.offPremiseOpen, "grid"],
    endpoints.restaurant.gridOffPremise,
    {
      enabled,
      retry: false,
    },
  );

  const notifications = useOptionalRestaurantNotifications();
  const chimeSound = notifications?.chimeSound;
  const startSoundReminder = notifications?.startSoundReminder;
  const toast = usePrivateToast();
  const t = useTranslate();
  const { locale } = useI18n();
  const navigate = useNavigate();
  const seen = useRef<Map<string, string> | null>(null);

  useEffect(() => {
    if (!enabled || !grid.data) return;
    const current = cashierRequestsOnFloor(grid.data, offPremise.data);
    const fresh = freshCashierRequests(seen.current, current);
    seen.current = new Map(current.map((request) => [request.invoiceId, request.requestedAt]));
    for (const request of fresh) {
      chimeSound?.(`cashier:${request.invoiceId}:${request.requestedAt}`);
      const place =
        request.tableName ?? t((m) => m.pos.invoiceNumber, { sequence: request.dailySequence });
      toast.info(t((m) => m.pos.cashierRequestAlert, { place }), undefined, {
        durationMs: null,
        onDismiss: startSoundReminder?.(),
        onClick: () =>
          navigate(`/${locale}/pos/invoices/${encodeURIComponent(request.invoiceId)}`),
      });
    }
  }, [chimeSound, startSoundReminder, enabled, grid.data, locale, navigate, offPremise.data, t, toast]);

  return null;
}
